import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { MenuButton, StyledMenu } from './styled';
import templateSvg from 'assets/template.svg';
import documentSvg from 'assets/document.svg';
import { Avatar, Box, Divider, Typography, MenuItem, ListItemIcon, ListItemText, Button } from '@mui/material';
import { Logout, SettingsOutlined } from '@mui/icons-material';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import { RouteList, AdminRouteList, User } from 'types';
import { useAuth } from 'hooks';
import { getUserInitials } from 'utils';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { SupportModeDeactivateButton } from '../SupportModeBanner/SupportModeDeactivateButton';

const getMenuItems = (user?: User) => [
  {
    iconSlot: <PersonOutlinedIcon sx={{ color: 'grey.800' }} />,
    title: 'Mein Benutzerkonto',
    route: RouteList.Profile,
  },
  {
    iconSlot: <img alt="template icon" src={documentSvg} />,
    title: 'Rechnungen',
    route: RouteList.Invoices,
  },
  {
    iconSlot: <img alt="template icon" src={templateSvg} />,
    title: 'Meine Kampagnen',
    route: RouteList.MyCampaigns,
  },
  ...(user?.isAdministrator
    ? [
        {
          iconSlot: <AdminPanelSettingsIcon />,
          title: 'Administration',
          route: `${AdminRouteList.Campaigns}`,
        },
      ]
    : []),
];

const WelcomeBox = ({ message, email }: { message: string; email: string }) => {
  return (
    <>
      <Box sx={{ pl: 5, pt: 5, pr: 5, pb: 2 }}>
        <Typography variant="h4">{message}</Typography>
        <Typography>{email}</Typography>
      </Box>
      <Divider sx={{ mx: 5, my: 2 }} />
    </>
  );
};

const SupportModeBox = ({ email }: { email: string }) => {
  return (
    <Box sx={{ pl: 5, pt: 5, pr: 5, pb: 3, background: (theme) => theme.palette.error.dark }}>
      <Typography variant="h4" color="#FFFFFF">
        Eingeloggt im Namen von
      </Typography>
      <Typography variant="h4" color="#FFFFFF">
        {email}
      </Typography>
      <Box sx={{ mt: 2 }}>
        <SupportModeDeactivateButton />
      </Box>
    </Box>
  );
};

export const UserAccount = () => {
  const { user, impersonator } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const menuItems = getMenuItems(user);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClearStorage = () => {
    localStorage.removeItem('isFeedbackShownInThisSession');
  };

  const welcomeMessage = user?.firstName ? `Willkommen, ${user.firstName}` : 'Willkommen';

  return user ? (
    <Box>
      <MenuButton onClick={handleClick}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>{getUserInitials(user)}</Avatar>
        <SettingsOutlined color="primary" sx={{ ml: 4, width: 24, height: 24 }} />
      </MenuButton>

      <StyledMenu open={open} anchorEl={anchorEl} onClose={handleClose}>
        {!!impersonator ? (
          <SupportModeBox email={user?.email} />
        ) : (
          <WelcomeBox message={welcomeMessage} email={user?.email} />
        )}
        {menuItems.map((menuItem) => (
          <MenuItem
            key={`menu-item-${menuItem.route}`}
            component={NavLink}
            to={menuItem.route}
            end
            onClick={handleClose}
          >
            <ListItemIcon>{menuItem.iconSlot}</ListItemIcon>
            <ListItemText>{menuItem.title}</ListItemText>
          </MenuItem>
        ))}
        <Divider sx={{ mx: 5 }} />
        <MenuItem component={Link} to={RouteList.Logout} onClick={handleClearStorage}>
          <ListItemIcon>
            <Logout />
          </ListItemIcon>
          <ListItemText>Log out</ListItemText>
        </MenuItem>
      </StyledMenu>
    </Box>
  ) : (
    <Button
      variant="contained"
      onClick={() => {
        navigate(RouteList.Login, { state: { redirectTo: window.location.href } });
      }}
    >
      Login
    </Button>
  );
};
